import {
  Snackbar,
  Alert,
  CircularProgress,
  Backdrop,
  Icon,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  MenuItem,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  InputAdornment,
} from "@mui/material";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDInput from "theme/components/MDInput";
import MDButton from "theme/components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import axios from "axios.js";
import usePageTitle from "app/pageTitle";

import DataTable from "theme/Tables/DataTable";
import { useAwaitDialog } from "app/components/status_dialog";

export default function OpsSalesRulesPage() {
  const title = "Sales Rules";
  usePageTitle(title);

  const navigate = useNavigate();
  const statusDialog = useAwaitDialog();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [refreshPage, setRefreshPage] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);

  const [salesRules, setSalesRules] = useState([]);
  const [currency, setCurrency] = useState("");
  const [openRuleDialog, setOpenRuleDialog] = useState(false);
  const [editingRuleRef, setEditingRuleRef] = useState(null);

  // Form states
  const [ruleTitle, setRuleTitle] = useState("");
  const [priceModel, setPriceModel] = useState("standard");
  const [enableDiscount, setEnableDiscount] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [isDiscountPercentage, setIsDiscountPercentage] = useState(false);
  const [paymentEnabled, setPaymentEnabled] = useState(true);
  const [inventoryTracked, setInventoryTracked] = useState(false);
  const [presenceRequired, setPresenceRequired] = useState(false);
  const [status, setStatus] = useState(true);

  const resetForm = () => {
    setEditingRuleRef(null);
    setRuleTitle("");
    setPriceModel("standard");
    setEnableDiscount(false);
    setDiscount(0);
    setIsDiscountPercentage(false);
    setPaymentEnabled(true);
    setInventoryTracked(false);
    setPresenceRequired(false);
    setStatus(true);
  };

  async function deleteSalesRule(ruleRef) {
    setShowLoading(true);
    await axios
      .get(`/portal/ops/sales/rules/${ruleRef}/delete.json`)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  async function toggleRuleStatus(ruleRow) {
    setShowLoading(true);
    const json = {
      reference: ruleRow.reference,
      title: ruleRow.title,
      price_model: ruleRow.price_model,
      discount: ruleRow.discount,
      is_discount_percentage: ruleRow.is_discount_percentage,
      payment_enabled: ruleRow.payment_enabled,
      inventory_tracked: ruleRow.inventory_tracked,
      presence_required: ruleRow.presence_required,
      status: !ruleRow.status,
    };

    await axios
      .post("/portal/ops/sales/rules/edit.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  const salesRulesTableColumns = [
    { Header: "Title", accessor: "title", align: "left" },
    { Header: "Price Model", accessor: "price_model", align: "left" },
    { Header: "Payment Enabled", accessor: "payment_enabled", align: "center" },
    { Header: "Inventory Tracking", accessor: "inventory_tracked", align: "center" },
    { Header: "Presence Required", accessor: "presence_required", align: "center" },
    { Header: "Status", accessor: "status", align: "center" },
    { Header: "Actions", accessor: "actions", align: "right" },
  ];

  const buildSalesRulesRows = (data) => {
    const rows = [];
    data.map((row) => {
      rows.push({
        title: row.title,
        price_model: row.price_model,
        payment_enabled: (
          <Icon color={row.payment_enabled ? "success" : "error"}>
            {row.payment_enabled ? "check_circle" : "cancel"}
          </Icon>
        ),
        inventory_tracked: (
          <Icon color={row.inventory_tracked ? "success" : "error"}>
            {row.inventory_tracked ? "check_circle" : "cancel"}
          </Icon>
        ),
        presence_required: (
          <Icon color={row.presence_required ? "success" : "error"}>
            {row.presence_required ? "check_circle" : "cancel"}
          </Icon>
        ),
        status: <Switch checked={row.status} onChange={() => toggleRuleStatus(row)} color="dark" />,
        actions: (
          <MDBox sx={{ width: "100%" }}>
            <MDBox mt={1} display="flex" gap={0.7} alignItems="center">
              <Tooltip title="Edit Sales Rule">
                <MDButton
                  size="small"
                  variant="outlined"
                  color="warning"
                  iconOnly
                  onClick={() => handleOpenEditDialog(row)}
                >
                  <Icon sx={{ cursor: "pointer" }} fontSize="small">
                    edit
                  </Icon>
                </MDButton>
              </Tooltip>
              <Tooltip title="Manage Rule">
                <MDButton
                  size="small"
                  variant="outlined"
                  color="dark"
                  iconOnly
                  onClick={() => navigate(`/ops/sales-rules/${row.reference}`)}
                >
                  <Icon sx={{ cursor: "pointer" }} fontSize="small">
                    settings
                  </Icon>
                </MDButton>
              </Tooltip>
              <Tooltip title="Delete Sales Rule?">
                <MDButton
                  size="small"
                  variant="outlined"
                  color="error"
                  iconOnly
                  onClick={() => {
                    if (window.confirm(`Delete sales rule?`) === true) {
                      deleteSalesRule(row.reference);
                    }
                  }}
                >
                  <Icon sx={{ cursor: "pointer" }} fontSize="small">
                    delete
                  </Icon>
                </MDButton>
              </Tooltip>
            </MDBox>
          </MDBox>
        ),
      });
      return null;
    });

    return rows;
  };

  const handleOpenEditDialog = (rule) => {
    setEditingRuleRef(rule.reference);
    setRuleTitle(rule.title || "");
    setPriceModel(rule.price_model || "standard");

    // Check if rule has an active discount set
    const hasDiscount = Boolean(rule.discount && rule.discount > 0);
    setEnableDiscount(hasDiscount);
    setDiscount(rule.discount || 0);
    setIsDiscountPercentage(rule.is_discount_percentage || false);

    setPaymentEnabled(rule.payment_enabled ?? true);
    setInventoryTracked(rule.inventory_tracked ?? false);
    setPresenceRequired(rule.presence_required ?? false);
    setStatus(rule.status ?? true);
    setOpenRuleDialog(true);
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get("portal/ops/sales/rules.json")
        .then((result) => {
          setShowLoading(false);
          console.log(result.data.data);
          if (result.data.status === "success") {
            // Set currency from backend response
            if (result.data.currency) {
              setCurrency(result.data.currency);
            }

            const ruleRows = buildSalesRulesRows(result.data.data);
            setSalesRules(ruleRows);
            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage]);

  async function saveSalesRule() {
    if (!ruleTitle) {
      setSeverity("error");
      setErrMsg("Title is required.");
      setOpen(true);
      return;
    }

    setOpenRuleDialog(false);
    setShowLoading(true);

    const json = {
      reference: editingRuleRef,
      title: ruleTitle,
      price_model: priceModel,
      discount: enableDiscount ? parseFloat(discount) : 0,
      is_discount_percentage: enableDiscount ? isDiscountPercentage : false,
      payment_enabled: paymentEnabled,
      inventory_tracked: inventoryTracked,
      presence_required: presenceRequired,
    };

    const endpoint = editingRuleRef
      ? "/portal/ops/sales/rules/edit.json"
      : "/portal/ops/sales/rules/create.json";

    await axios
      .post(endpoint, json)
      .then(async (result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          await statusDialog.show(result.data.data.message, "success", "Success!");

          resetForm();
          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      <Dialog open={openRuleDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>{editingRuleRef ? "Edit Sales Rule" : "Create Sales Rule"}</MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox>
            {/* Section 1: Basic Information */}
            <MDTypography
              variant="caption"
              color="text"
              fontWeight="bold"
              textTransform="uppercase"
            >
              General Details
            </MDTypography>
            <MDBox mt={1}>
              <MDInput
                name="title"
                margin="dense"
                label="Title"
                type="text"
                fullWidth
                variant="outlined"
                value={ruleTitle}
                onChange={(e) => setRuleTitle(e.target.value)}
              />
            </MDBox>
            <MDBox mt={3}>
              <MDInput
                select
                label="Price Model"
                value={priceModel}
                onChange={(e) => setPriceModel(e.target.value)}
                fullWidth
                SelectProps={{ style: { padding: "12px 0px" } }}
              >
                <MenuItem value="FIXED">Fixed Pricing</MenuItem>
                <MenuItem value="OPEN">Variable Pricing</MenuItem>
              </MDInput>
            </MDBox>

            <Divider sx={{ my: 2 }} />

            {/* Section 2: Discount Settings */}
            <MDTypography
              variant="caption"
              color="text"
              fontWeight="bold"
              textTransform="uppercase"
            >
              Discount Configuration
            </MDTypography>
            <MDBox mt={1}>
              <FormControlLabel
                control={
                  <Switch
                    checked={enableDiscount}
                    onChange={(e) => {
                      const enabled = e.target.checked;
                      setEnableDiscount(enabled);
                      if (!enabled) {
                        setDiscount(0);
                      }
                    }}
                    color="dark"
                  />
                }
                label="Enable Discount"
              />
            </MDBox>

            {enableDiscount && (
              <>
                <MDBox mt={1}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isDiscountPercentage}
                        onChange={(e) => setIsDiscountPercentage(e.target.checked)}
                        color="dark"
                      />
                    }
                    label="Is Discount Percentage?"
                  />
                </MDBox>
                <MDBox mt={1}>
                  <MDInput
                    name="discount"
                    margin="dense"
                    label="Discount"
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={discount}
                    onChange={(e) => setDiscount(e.target.value)}
                    InputProps={{
                      startAdornment:
                        !isDiscountPercentage && currency ? (
                          <InputAdornment position="start">
                            <MDTypography variant="body2">{currency}</MDTypography>
                          </InputAdornment>
                        ) : null,
                      endAdornment: isDiscountPercentage ? (
                        <InputAdornment position="end">%</InputAdornment>
                      ) : null,
                    }}
                  />
                </MDBox>
              </>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Section 3: Policy & Rules Switches */}
            <MDTypography
              variant="caption"
              color="text"
              fontWeight="bold"
              textTransform="uppercase"
            >
              Policy & Controls
            </MDTypography>
            <MDBox mt={1} display="flex" flexDirection="column">
              <FormControlLabel
                control={
                  <Switch
                    checked={paymentEnabled}
                    onChange={(e) => setPaymentEnabled(e.target.checked)}
                    color="dark"
                  />
                }
                label="Payment Enabled"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={inventoryTracked}
                    onChange={(e) => setInventoryTracked(e.target.checked)}
                    color="dark"
                  />
                }
                label="Inventory Tracked"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={presenceRequired}
                    onChange={(e) => setPresenceRequired(e.target.checked)}
                    color="dark"
                  />
                }
                label="Physical Presence Required"
              />
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenRuleDialog(false);
              resetForm();
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="dark"
            variant="contained"
            onClick={() => {
              saveSalesRule();
            }}
          >
            {editingRuleRef ? "Update" : "Create"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {pageLoaded && (
        <MDBox mb={5}>
          <MDBox mt={0} mb={1} display="flex" justifyContent="space-between">
            <MDBox>
              <Button
                color="dark"
                size="small"
                startIcon={<Icon>west</Icon>}
                onClick={() => {
                  navigate(`/ops`);
                }}
              >
                Back
              </Button>
            </MDBox>
            <MDBox>
              <Button color="dark" size="small" startIcon={<Icon>gavel</Icon>}>
                Sales Rules
              </Button>
            </MDBox>
          </MDBox>

          <MDBox mt={2}>
            <MDBox>
              <Card sx={{ height: "100%", marginTop: "0px" }}>
                <MDBox p={2}>
                  <MDBox align="center">
                    <MDBox>
                      <MDTypography variant="h6" fontSize={48}>
                        <Icon>gavel</Icon>
                      </MDTypography>
                      <MDTypography variant="h6" fontWeight="light" fontSize={16}>
                        Configure sales rules for your platform operations.
                        <br />
                        Control pricing models, discounts, inventory tracking, and payment policies.
                      </MDTypography>
                    </MDBox>
                    <MDBox mt={3} mb={3}>
                      <MDButton
                        variant="contained"
                        color="dark"
                        onClick={() => {
                          resetForm();
                          setOpenRuleDialog(true);
                        }}
                        startIcon={<Icon>add</Icon>}
                      >
                        Add Sales Rule
                      </MDButton>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Card>
            </MDBox>
            <MDBox mt={3}>
              <Card sx={{ height: "100%", marginTop: "0px" }}>
                <MDBox>
                  <MDBox p={2}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center">
                      <MDBox>
                        <MDTypography variant="h6" fontWeight="medium">
                          Sales Rules
                        </MDTypography>
                      </MDBox>
                      <MDBox>
                        <MDButton
                          variant="contained"
                          color="dark"
                          iconOnly
                          size="small"
                          onClick={() => {
                            resetForm();
                            setOpenRuleDialog(true);
                          }}
                        >
                          <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                        </MDButton>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                  <MDBox>
                    <DataTable
                      table={{ columns: salesRulesTableColumns, rows: salesRules }}
                      isSorted
                      entriesPerPage={false}
                      showTotalEntries
                      buttons
                      noEndBorder
                    />
                  </MDBox>
                </MDBox>
              </Card>
            </MDBox>
          </MDBox>
        </MDBox>
      )}

      <Footer />
    </DashboardLayout>
  );
}
